import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { ChatService } from "services";
import { ChatResponse } from "types";
import { AppUtils } from "utils";

const { REQUEST_CHATS_LIST_ID } = ServiceConstants;

const getDashboardChats = (data: unknown): ChatResponse[] => {
  if (Array.isArray((data as { content?: unknown[] } | undefined)?.content)) {
    return (data as { content: ChatResponse[] }).content;
  }

  if (Array.isArray(data)) {
    return data as ChatResponse[];
  }

  return [];
};

const getLatestMessageTimestamp = (chat: ChatResponse) =>
  chat.lastMessage ? new Date(chat.lastMessage.createdAt).getTime() : 0;

const useDashboardChats = () => {
  const chatsServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CHATS_LIST_ID],
  );
  const [chats, setChats] = useState<ChatResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    ChatService.chatsAll({ page: 0, search: "" });
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      chatsServiceObject,
      REQUEST_CHATS_LIST_ID,
      (data) => {
        setChats(getDashboardChats(data));
        setLoading(false);
      },
      () => {
        setChats([]);
        setLoading(false);
      },
    );
  }, [chatsServiceObject]);

  const unreadChats = useMemo(
    () =>
      chats
        .filter((chat) => chat.unread > 0 && chat.lastMessage)
        .sort((firstChat, secondChat) => {
          return getLatestMessageTimestamp(secondChat) - getLatestMessageTimestamp(firstChat);
        }),
    [chats],
  );

  const unreadMessagesCount = useMemo(
    () => chats.reduce((sum, chat) => sum + (chat.unread ?? 0), 0),
    [chats],
  );

  return {
    loading,
    unreadChats,
    unreadMessagesCount,
  };
};

export default useDashboardChats;
